import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { useResizeObserver, useValueEffect } from './';

export type UseVisibleTruncatedChildrenRenderOverflowElementParam = {
  visibleItemCount: number;
  childArray: React.ReactElement[];
};

export type UseVisibleTruncatedChildrenParam = {
  children: React.ReactNode;
  indicesToShow?: number[];
  renderOverflowElement?: (
    props: UseVisibleTruncatedChildrenRenderOverflowElementParam,
  ) => React.ReactElement;
  overflowElementPosition?: number;
  containerRef: React.RefObject<HTMLElement>;
  minVisibleItems?: number;
  maxVisibleItems?: number;
  forceLastItemVisibility?: boolean;
  pixelGap?: number;
};

export const useVisibleTruncatedChildren = ({
  children,
  indicesToShow,
  renderOverflowElement,
  overflowElementPosition,
  containerRef,
  minVisibleItems = 1,
  maxVisibleItems,
  forceLastItemVisibility = false,
  pixelGap = 0,
}: UseVisibleTruncatedChildrenParam) => {
  /**
   * Verify that indices are consecutive, contained in the children array, are unique, and start
   * at 0 e.g. [0] or [0, 1]
   */
  const sortedIndicesToShow = useMemo(() => {
    const out = [...(indicesToShow ?? [])].sort((a, b) => a - b);
    if (out.length > 0) {
      if (out[0] !== 0) {
        throw new Error('indicesToShow must start at 0.');
      }

      for (let i = 0; i < out.length; i++) {
        if (out[i] !== i) {
          throw new Error('indicesToShow must be consecutive and unique.');
        }
      }
    }
    return out;
  }, [indicesToShow]);

  // Not using React.Children.toArray because it mutates the key prop.
  const childArray: React.ReactElement<any>[] = [];
  Children.forEach(children, (child, index) => {
    if (isValidElement(child)) {
      if (child.key == null) {
        // eslint-disable-next-line react/no-array-index-key, no-param-reassign
        child = cloneElement(child, { key: index });
      }
      childArray.push(child);
    }
  });

  const [visibleItemCount, setVisibleItemCount] = useValueEffect(
    childArray.length,
  );

  /**
   * Computes how many items in the children array should render, weaving in the
   * overflow elements and the elements to show based on props along the way.
   * Relies on the containerRef children elements to compute widths.
   */
  const updateOverflow = useCallback(() => {
    const computeVisibleItems = (desiredVisibleItemsCount: number): number => {
      // Refs can be null at runtime.
      const currListRef: HTMLElement | null = containerRef.current;
      if (!currListRef) {
        return desiredVisibleItemsCount;
      }

      const currentChildrenElementList = Array.from(
        currListRef.children,
      ) as HTMLLIElement[];
      if (currentChildrenElementList.length <= 0) {
        return desiredVisibleItemsCount;
      }

      const containerWidth = currListRef.offsetWidth + 1;

      let calculatedWidth = 0;
      let newVisibleItemCount = 0;
      let adjustedOverflowPosition =
        overflowElementPosition ?? currentChildrenElementList.length - 1;

      // if sortedIndices provided, give them preferential rendering treatment
      // NOTE: need to do this step first so we know what order in the array the overflow element
      //  should occupy
      let needsGap = false;
      sortedIndicesToShow.forEach(() => {
        if (needsGap) {
          calculatedWidth += pixelGap;
        } else {
          needsGap = true;
        }
        calculatedWidth += currentChildrenElementList[0].offsetWidth;
        if (calculatedWidth <= containerWidth) {
          currentChildrenElementList.shift();
          newVisibleItemCount++;
          adjustedOverflowPosition--;
        }
      });

      let maxVisibleItemCount = maxVisibleItems;
      // Handle overflow element width contribution
      const isShowingOverflowElement =
        renderOverflowElement != null &&
        childArray.length > desiredVisibleItemsCount;
      if (isShowingOverflowElement) {
        const overflowElement = currentChildrenElementList.splice(
          adjustedOverflowPosition,
          1,
        )[0];
        calculatedWidth += overflowElement!.offsetWidth;

        if (needsGap) {
          calculatedWidth += pixelGap;
        } else {
          needsGap = true;
        }

        if (maxVisibleItemCount != null) {
          maxVisibleItemCount--;
        }
      }

      // If the overflow item width contribution goes over the container width, that means
      //  we cannot actually render all of the preferentially treated indicesToShow above. Thus
      //  we will decrement the count of items we can show
      //  NOTE: This assumes that the overflow element is smaller than the element that was
      //   conditionally added to the render list before it. If this is not the case, you can
      //   run into odd rendering side effects.
      if (sortedIndicesToShow.length > 0 && calculatedWidth > containerWidth) {
        newVisibleItemCount--;
      }

      // If we force visibility of last item, ensure its width is pre-computed to the total
      if (forceLastItemVisibility && currentChildrenElementList.length > 0) {
        // Ensure the last breadcrumb isn't truncated when we measure it.
        const last = currentChildrenElementList.pop() as HTMLLIElement;
        last.style.overflow = 'visible';

        calculatedWidth += last.offsetWidth;
        newVisibleItemCount++;

        last.style.overflow = '';
      }

      // iterate through remaining elements and determine how many are visible
      for (const breadcrumb of currentChildrenElementList) {
        if (needsGap) {
          calculatedWidth += pixelGap;
        } else {
          needsGap = true;
        }
        calculatedWidth += breadcrumb.offsetWidth;
        if (calculatedWidth <= containerWidth) {
          newVisibleItemCount++;
        }
      }

      if (maxVisibleItemCount != null) {
        newVisibleItemCount = Math.min(
          maxVisibleItemCount,
          newVisibleItemCount,
        );
      }

      if (minVisibleItems != null) {
        newVisibleItemCount = Math.max(minVisibleItems, newVisibleItemCount);
      }

      return newVisibleItemCount;
    };

    setVisibleItemCount(function* () {
      // Update to show all items.
      yield childArray.length;

      // Measure, and update to show the items that fit.
      const computedVisibleItemCount = computeVisibleItems(childArray.length);
      yield computedVisibleItemCount;

      // If the number of items is less than the number of children,
      // then update again to ensure that the menu fits.
      if (
        computedVisibleItemCount < childArray.length &&
        computedVisibleItemCount > 1
      ) {
        const newComputedVisibleItemCount = computeVisibleItems(
          computedVisibleItemCount,
        );
        yield newComputedVisibleItemCount;
      }
    });
  }, [
    setVisibleItemCount,
    containerRef,
    maxVisibleItems,
    renderOverflowElement,
    childArray.length,
    sortedIndicesToShow,
    minVisibleItems,
    overflowElementPosition,
    forceLastItemVisibility,
    pixelGap,
  ]);

  useResizeObserver({ ref: containerRef, onResize: updateOverflow });

  const lastChildren = useRef<typeof children | null>(null);
  useLayoutEffect(() => {
    if (children !== lastChildren.current) {
      lastChildren.current = children;
      updateOverflow();
    }
  });

  let truncatedChildren = childArray;
  if (visibleItemCount < childArray.length) {
    // Only show the visible items.
    if (sortedIndicesToShow[0] === 0 || overflowElementPosition === 0) {
      if (sortedIndicesToShow.length >= visibleItemCount) {
        truncatedChildren = childArray.slice(0, visibleItemCount);
      } else {
        const childrenToKeep = childArray.slice(0, sortedIndicesToShow.length);
        truncatedChildren = childrenToKeep.concat(
          childArray.slice(-(visibleItemCount - sortedIndicesToShow.length)),
        );
      }
    } else {
      truncatedChildren = childArray.slice(
        0,
        sortedIndicesToShow.length + visibleItemCount,
      );
    }
    if (renderOverflowElement) {
      if (overflowElementPosition == null) {
        truncatedChildren.push(
          renderOverflowElement({ visibleItemCount, childArray }),
        );
      } else {
        truncatedChildren.splice(
          overflowElementPosition ?? truncatedChildren.length,
          0,
          renderOverflowElement({ visibleItemCount, childArray }),
        );
      }
    }
  }

  return { visibleItemCount, childArray, truncatedChildren };
};

export type UseVisibleTruncatedChildrenResult = ReturnType<
  typeof useVisibleTruncatedChildren
>;
